import * as THREE from 'three';

/**
 * Handles all teleportation and safe positioning logic
 */
export class TeleportationSystem {
  constructor(scene, camera) {
    this.scene = scene;
    this.camera = camera;
    this.raycaster = new THREE.Raycaster();
  }

  /**
   * Check if a camera position is safe (not intersecting walls or outside room bounds)
   */
  isPositionSafe(position, minDistanceFromWalls = 1.5, floor = null, customWalls = null) {
    // For procedural rooms, check against room boundaries
    if (!customWalls) {
      if (!floor) return true;
      
      const floorGeometry = floor.geometry;
      if (floorGeometry && floorGeometry.parameters) {
        const { width, height } = floorGeometry.parameters;
        const halfWidth = width / 2;
        const halfDepth = height / 2;
        
        return (
          position.x > -halfWidth + minDistanceFromWalls &&
          position.x < halfWidth - minDistanceFromWalls &&
          position.z > -halfDepth + minDistanceFromWalls &&
          position.z < halfDepth - minDistanceFromWalls
        );
      }
      return true;
    }
    
    // For custom rooms, use raycasting to detect wall intersections
    return this.checkWallIntersections(position, minDistanceFromWalls);
  }
  
  /**
   * Check for wall intersections using raycasting in all directions
   */
  checkWallIntersections(position, minDistance) {
    const directions = [
      new THREE.Vector3(1, 0, 0),   // Right
      new THREE.Vector3(-1, 0, 0),  // Left
      new THREE.Vector3(0, 0, 1),   // Forward
      new THREE.Vector3(0, 0, -1),  // Backward
      new THREE.Vector3(0.707, 0, 0.707),   // Diagonal NE
      new THREE.Vector3(-0.707, 0, 0.707),  // Diagonal NW
      new THREE.Vector3(0.707, 0, -0.707),  // Diagonal SE
      new THREE.Vector3(-0.707, 0, -0.707), // Diagonal SW
    ];
    
    // Get all wall meshes from the scene
    const wallMeshes = this.getWallMeshes();
    console.log(`Checking wall intersections at position (${position.x.toFixed(2)}, ${position.z.toFixed(2)}) with ${wallMeshes.length} wall meshes`);
    
    // Check each direction for wall intersections
    for (const direction of directions) {
      this.raycaster.set(position, direction);
      const intersects = this.raycaster.intersectObjects(wallMeshes, false);
      
      if (intersects.length > 0 && intersects[0].distance < minDistance) {
        console.log(`Wall too close: ${intersects[0].distance.toFixed(2)} < ${minDistance} in direction (${direction.x}, ${direction.z})`);
        return false; // Too close to a wall
      }
    }
    
    return true; // Safe position
  }
  
  /**
   * Get all wall meshes from the scene or from specific objects
   * @param {THREE.Object3D[]} specificObjects - Optional array to search instead of entire scene
   */
  getWallMeshes(specificObjects = null) {
    const wallMeshes = [];
    
    if (specificObjects) {
      // Search only in provided objects (for multi-room mode)
//      console.log(`🔍 Searching for walls in ${specificObjects.length} specific objects (visible room only)`);
      specificObjects.forEach(obj => {
        if (obj.isMesh) {
          const isWall = this.isWallMesh(obj);
          if (isWall) {
            wallMeshes.push(obj);
          }
        }
      });
    } else {
      // Search entire scene (traditional mode)
//      console.log('🔍 Searching for walls in entire scene (traditional mode)');
      this.scene.traverse((child) => {
        if (child.isMesh && this.isWallMesh(child)) {
          wallMeshes.push(child);
        }
      });
    }
    
//    console.log(`Found ${wallMeshes.length} wall meshes`);
    return wallMeshes;
  }
  
  /**
   * Check if a mesh is a wall
   */
  isWallMesh(child) {
    return (
      child.name.toLowerCase().includes('wall') || 
      (child.geometry?.type === 'PlaneGeometry' && 
       !child.name.toLowerCase().includes('floor') && 
       !child.name.toLowerCase().includes('ceiling')) ||
      (child.material && child.material.map && 
       child.material.map.image && 
       child.material.map.image.src && 
       child.material.map.image.src.includes('V9wtIRM')) || // Wall texture check
      (child.position.y > 0.5 && child.geometry?.type === 'PlaneGeometry') // Vertical planes are likely walls
    );
  }
  
  /**
   * Check if there's a clear path between two positions (no walls in between)
   * @param {THREE.Vector3} fromPosition - Starting position
   * @param {THREE.Vector3} toPosition - Target position
   * @param {THREE.Object3D[]} specificObjects - Optional array of objects to check (for multi-room)
   */
  canTravelTo(fromPosition, toPosition, specificObjects = null) {
    const direction = new THREE.Vector3().subVectors(toPosition, fromPosition);
    const distance = direction.length();
    direction.normalize();
    
    // Set raycaster from current position towards target
    this.raycaster.set(fromPosition, direction);
    
    // Get wall meshes from specific objects or entire scene
    const wallMeshes = this.getWallMeshes(specificObjects);
    
    // Check for intersections along the path
    const intersects = this.raycaster.intersectObjects(wallMeshes, false);
    
    // If there are intersections and the first one is closer than our target distance
    if (intersects.length > 0 && intersects[0].distance < distance - 0.1) {
      return false; // Wall blocking the path
    }
    
    return true; // Clear path
  }
  
  /**
   * Check if there's a clear line of sight between viewer position and target (for viewing objects)
   */
  hasLineOfSight(viewerPosition, targetPosition) {
    const direction = new THREE.Vector3().subVectors(targetPosition, viewerPosition);
    const distance = direction.length();
    direction.normalize();
    
    // Set raycaster from viewer position towards target
    this.raycaster.set(viewerPosition, direction);
    
    // Get all wall meshes (but not floor or ceiling)
    const wallMeshes = this.getWallMeshes().filter(mesh => 
      !mesh.name.toLowerCase().includes('floor') && 
      !mesh.name.toLowerCase().includes('ceiling')
    );
    
    // Check for wall intersections that would block the view
    const intersects = this.raycaster.intersectObjects(wallMeshes, false);
    
    // If there are intersections closer than the target, view is blocked
    if (intersects.length > 0 && intersects[0].distance < distance - 0.1) {
      return false; // View blocked by wall
    }
    
    return true; // Clear line of sight
  }
  
  /**
   * Check if two positions are in the same room (for cross-room teleportation detection)
   */
  isInSameRoom(position1, position2, floor = null, customWalls = null) {
    // For procedural rooms, check if both positions are within the same room bounds
    if (!customWalls) {
      if (!floor) return true;
      
      const floorGeometry = floor.geometry;
      if (floorGeometry && floorGeometry.parameters) {
        const { width, height } = floorGeometry.parameters;
        const halfWidth = width / 2;
        const halfDepth = height / 2;
        
        const pos1InRoom = (
          position1.x > -halfWidth && position1.x < halfWidth &&
          position1.z > -halfDepth && position1.z < halfDepth
        );
        
        const pos2InRoom = (
          position2.x > -halfWidth && position2.x < halfWidth &&
          position2.z > -halfDepth && position2.z < halfDepth
        );
        
        return pos1InRoom && pos2InRoom;
      }
      return true;
    }
    
    // For custom rooms, check if there's a clear path without major wall intersections
    // This is a simplified check - could be enhanced with room boundary data
    return this.canTravelTo(position1, position2);
  }
  
  /**
   * Calculate distance from a point to a wall
   */
  calculateDistanceToWall(position, wall) {
    // Simplified distance calculation - can be enhanced based on wall geometry
    const dx = position.x - wall.position.x;
    const dz = position.z - wall.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }
  
  /**
   * Find a safe distance for camera positioning
   */
  findSafeDistance(objectPlacement, angle, minDistance, maxDistance, floor = null, customWalls = null) {
    for (let distance = minDistance; distance <= maxDistance; distance += 0.5) {
      const testPosition = new THREE.Vector3(
        objectPlacement.positionX + Math.sin(angle) * distance,
        1.8,
        objectPlacement.positionZ + Math.cos(angle) * distance
      );
      
      if (this.isPositionSafe(testPosition, 1.0, floor, customWalls)) { // Reduced safety margin
        return distance;
      }
    }
    
    return minDistance; // Fallback to minimum distance
  }
  
  /**
   * Find the nearest safe position to a target position
   */
  findNearestSafePosition(targetPosition, searchRadius = 3.0, stepSize = 0.5) {
    // First try: Find optimal position perpendicular to closest wall
    const bestWallPosition = this.findPositionPerpendicularToClosestWall(targetPosition, 2.0);
    if (bestWallPosition && this.isPositionSafe(bestWallPosition, 1.0)) {
      console.log('Found safe position perpendicular to closest wall:', bestWallPosition);
      return bestWallPosition;
    }
    
    // Second try: Search in a spiral pattern around the target position
    for (let radius = stepSize; radius <= searchRadius; radius += stepSize) {
      const steps = Math.max(8, Math.floor(radius * 8)); // More steps for larger radius
      
      for (let i = 0; i < steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        const testPosition = new THREE.Vector3(
          targetPosition.x + Math.cos(angle) * radius,
          targetPosition.y,
          targetPosition.z + Math.sin(angle) * radius
        );
        
        if (this.isPositionSafe(testPosition, 1.0)) {
          return testPosition;
        }
      }
    }
    
    return null; // No safe position found within search radius
  }

  /**
   * Find position perpendicular to the closest wall from target position
   * Uses wall surface normal to position camera away from wall at correct height
   */
  findPositionPerpendicularToClosestWall(targetPosition, distance = 2.5) {
    const wallMeshes = this.getWallMeshes();
    if (wallMeshes.length === 0) return null;
    
    console.log(`Finding closest wall to object at (${targetPosition.x.toFixed(2)}, ${targetPosition.z.toFixed(2)}) among ${wallMeshes.length} walls`);
    
    let closestWall = null;
    let closestDistance = Infinity;
    let wallNormal = null;
    let intersectionPoint = null;
    
    // Cast rays in multiple directions to find the closest wall and get proper surface normal
    const directions = [
      new THREE.Vector3(1, 0, 0),   // Right
      new THREE.Vector3(-1, 0, 0),  // Left  
      new THREE.Vector3(0, 0, 1),   // Forward
      new THREE.Vector3(0, 0, -1),  // Backward
      new THREE.Vector3(0.707, 0, 0.707),   // Diagonal NE
      new THREE.Vector3(-0.707, 0, 0.707),  // Diagonal NW
      new THREE.Vector3(0.707, 0, -0.707),  // Diagonal SE
      new THREE.Vector3(-0.707, 0, -0.707), // Diagonal SW
    ];
    
    // Cast rays from object position at camera height
    const rayOrigin = new THREE.Vector3(targetPosition.x, 1.8, targetPosition.z);
    
    for (const direction of directions) {
      this.raycaster.set(rayOrigin, direction);
      const intersects = this.raycaster.intersectObjects(wallMeshes, false);
      
      if (intersects.length > 0) {
        const intersect = intersects[0];
        if (intersect.distance < closestDistance) {
          closestDistance = intersect.distance;
          closestWall = intersect.object;
          intersectionPoint = intersect.point;
          
          // Get the surface normal from the intersection
          if (intersect.face && intersect.face.normal) {
            wallNormal = intersect.face.normal.clone();
            // Transform normal to world space
            if (intersect.object.matrixWorld) {
              wallNormal.transformDirection(intersect.object.matrixWorld);
            }
            wallNormal.normalize();
          } else {
            // Fallback: use opposite of ray direction
            wallNormal = direction.clone().negate();
          }
        }
      }
    }
    
    if (closestWall && wallNormal && intersectionPoint) {
      console.log(`Closest wall found at distance ${closestDistance.toFixed(2)}`);
      console.log(`Wall normal: (${wallNormal.x.toFixed(2)}, ${wallNormal.y.toFixed(2)}, ${wallNormal.z.toFixed(2)})`);
      
      // Position camera at specified distance from object, in direction of wall normal (away from wall)
      // This ensures: Wall <- Object <- Camera
      const safePosition = new THREE.Vector3(
        targetPosition.x + wallNormal.x * distance,
        1.8, // Always at camera height
        targetPosition.z + wallNormal.z * distance
      );
      
//      console.log(`Positioning camera at safe distance from object:`, safePosition);
      return safePosition;
    }
    
    console.warn('Could not find suitable wall normal for teleportation');
    return null;
  }
}